/**
 * Reviews — v2 frontend types.
 *
 * Mirrors the backend `UnifiedReviewItem` read-model
 * (`apps/operations/src/modules/reviews/interfaces/review.interfaces.ts`)
 * and the request/response shapes for the admin endpoints under
 * `/v2/reviews` and `/v2/review-platforms/replies`.
 *
 * Date fields are wire-format strings (ISO-8601). Components convert as needed.
 */

// ─── Enums (mirrors of Prisma enums on the backend) ──────────────────────────

export type ReviewPlatform = 'GOOGLE' | 'FACEBOOK' | 'YELP' | 'BBB' | 'THUMBTACK';

/**
 * `UNCONFIRMED` is not a failure: the reply may already be live on the platform
 * and we cannot tell, so sending again risks a duplicate public comment.
 */
export type ReplyStatus = 'NONE' | 'AI_DRAFTED' | 'POSTED' | 'FAILED' | 'UNCONFIRMED';

export type UnifiedReviewSource =
  | 'EXTERNAL'
  | 'INTERNAL_PRIVATE'
  | 'INTERNAL_PUBLIC_INTENT'
  | 'TESTIMONIAL';

/** Free-form sentiment-tag enum used for filtering private feedback. */
export const SENTIMENT_TAGS = [
  'timeliness',
  'pricing',
  'quality',
  'tech_behavior',
  'scheduling',
  'other',
] as const;
export type SentimentTag = (typeof SENTIMENT_TAGS)[number];

// ─── Unified read-model ──────────────────────────────────────────────────────

export interface UnifiedReviewItem {
  /**
   * Composite ID (`ext_<uuid>` | `rsp_<uuid>` | `tst_<uuid>`). Use
   * `parseUnifiedReviewId` to split.
   */
  id: string;
  source: UnifiedReviewSource;
  platform?: ReviewPlatform;
  platformConfigId?: string;
  rating: number;
  text?: string;
  author?: string;
  /** ISO-8601 timestamp. */
  postedAt: string;
  replyStatus?: ReplyStatus;
  /**
   * `TESTIMONIAL` rows only — whether a human has cleared this one for the
   * anonymous job-stories page. Undefined on every other source, which have no
   * approval step, so treat `undefined` as "not applicable" rather than "no".
   */
  isApprovedForDisplay?: boolean;
  /**
   * Whether the customer agreed to public display. Only set for review
   * responses, which are the one source where consent is collected separately
   * from approval — a row can be consented and unapproved, or neither.
   *
   * Carried on the list row so the drawer has something to read while its
   * detail query is in flight; defaulting to `false` there made a consented,
   * already-public response render as neither. See #2427.
   */
  publishConsent?: boolean;
  linkedAppointmentId?: string;
  /**
   * The parent job of `linkedAppointmentId`, resolved server-side.
   *
   * Both are needed to link: the appointment route is nested under its job
   * (`/dashboard/jobs/$id/appointments/$appointmentId`). `linkedJobNumber` is
   * what a dispatcher recognises — a UUID fragment tells them nothing. Either
   * may be absent when the appointment is gone or has no job.
   */
  linkedJobId?: string;
  linkedJobNumber?: string;
  linkedTechId?: string;
  linkedTechName?: string;
  platformUrl?: string;
  /**
   * `EXTERNAL` rows only — ISO timestamp of when a complete walk of the
   * connected listing stopped returning this review. Absent while it is still
   * carried, which is the ordinary case.
   *
   * These rows stay in the list but are excluded from the stats strip above it,
   * so the row has to say why; see `ExternalReview.delistedAt` in operations.
   */
  delistedAt?: string;
  /**
   * `EXTERNAL` rows only — how the link to a visit was decided.
   *
   * Absent means no attribution was ever attempted, which is the honest reading for
   * rows ingested before the column existed. It must render as silence, never as a
   * verified link.
   */
  attributionConfidence?: 'CLICK_ATTRIBUTED' | 'INFERRED' | 'MANUAL' | 'UNATTRIBUTED';
  /** `EXTERNAL` rows only — visits that competed when attribution was refused. */
  ambiguousCandidateCount?: number;
  sentimentTags?: string[];
}

export interface ReviewStoryAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  downloadUrl: string;
  createdAt: string;
  /**
   * A file inherits the ACL of the note it hangs off. Only `PUBLIC_SAFE`
   * photos reach the anonymous share page; the authenticated dashboard
   * receives every photo, tagged.
   */
  visibility?: 'INTERNAL' | 'PUBLIC_SAFE';
}

export interface ReviewStoryNote {
  id: string;
  content?: string;
  createdAt: string;
  pinned: boolean;
}

export interface ReviewStoryItem {
  id: string;
  appointmentId: string;
  review: UnifiedReviewItem;
  job?: {
    id: string;
    jobNumber: string;
    name: string;
    description?: string;
    serviceName?: string;
    jobTypeName?: string;
    customerName?: string;
    customerId?: string;
    customerEmail?: string;
    technicianName?: string;
    completedAt?: string;
  };
  location?: {
    address: string;
    city?: string;
    state?: string;
    postalCode?: string;
    lat?: number;
    lng?: number;
  };
  notes: ReviewStoryNote[];
  photos: ReviewStoryAttachment[];
}

export interface ReviewStoryListResponse {
  items: ReviewStoryItem[];
  nextCursor?: string;
}

export interface ReviewStoryApiResponse {
  data: ReviewStoryItem[];
  nextCursor?: string;
}

export interface UnifiedReviewListResponse {
  data: UnifiedReviewItem[];
  nextCursor?: string;
}

/**
 * Splits a composite id like `ext_abc123` into `{ kind: 'external', id: 'abc123' }`.
 * Returns null for malformed input.
 */
export function parseUnifiedReviewId(
  composite: string,
): { kind: 'external' | 'response' | 'testimonial'; id: string } | null {
  if (composite.startsWith('ext_')) return { kind: 'external', id: composite.slice(4) };
  if (composite.startsWith('rsp_')) return { kind: 'response', id: composite.slice(4) };
  if (composite.startsWith('tst_')) return { kind: 'testimonial', id: composite.slice(4) };
  return null;
}

// ─── List filters (mirrors `ListUnifiedReviewsDto`) ──────────────────────────

export interface UnifiedReviewListParams {
  platforms?: ReviewPlatform[];
  sources?: UnifiedReviewSource[];
  minRating?: number;
  maxRating?: number;
  replyStatuses?: ReplyStatus[];
  postedAfter?: string;
  postedBefore?: string;
  /**
   * A rolling window in days, resolved server-side. Sent INSTEAD of
   * `postedAfter` for the rolling presets: a constant keeps the infinite
   * query's cache key stable across pages, while the server derives the bound
   * per request so the window still advances and matches `/stats`.
   */
  windowDays?: number;
  sentimentTag?: SentimentTag | string;
  techId?: string;
  appointmentId?: string;
  reviewer?: string;
  /** Narrows testimonials to those still awaiting approval for the public page. */
  pendingApprovalOnly?: boolean;
  /**
   * Narrows to public reviews still owed a service-recovery reply: at or below
   * the tenant's threshold and not yet marked handled. The threshold lives on
   * the server, so no rating bound travels with this.
   */
  needsRecovery?: boolean;
  limit?: number;
  cursor?: string;
}

// ─── Detail shapes ───────────────────────────────────────────────────────────

/**
 * `ReviewResponse` row returned from `GET /v2/reviews/response/:id`.
 * Mirrors the Prisma row shape (Json columns parsed where convenient).
 */
export interface ReviewResponseDetail {
  id: string;
  tenantId: string;
  appointmentId: string;
  contactId?: string | null;
  reminderId?: string | null;
  rating: number;
  feedbackText?: string | null;
  isPublic: boolean;
  externalPlatform?: ReviewPlatform | null;
  externalClickedAt?: string | null;
  sentimentAnalysis?: ReviewResponseSentimentAnalysis | null;
  internalTaskId?: string | null;
  /** The customer agreed this may be published. Only they can set it. */
  publishConsent?: boolean;
  publishConsentAt?: string | null;
  /** Staff clearance. Publication requires this AND `publishConsent`. */
  isApprovedForDisplay?: boolean;
  approvedAt?: string | null;
  approvedByUserId?: string | null;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExternalReviewDetail {
  id: string;
  tenantId: string;
  platform: ReviewPlatform;
  externalReviewId: string;
  rating: number;
  text?: string | null;
  author?: string | null;
  authorAvatarUrl?: string | null;
  postedAt: string;
  replyText?: string | null;
  replyDraft?: ReplyDraft | null;
  replyStatus: ReplyStatus;
  replyPostedAt?: string | null;
  replyError?: string | null;
  linkedAppointmentId?: string | null;
  linkedTechId?: string | null;
  linkedTechName?: string | null;
  linkedJobId?: string | null;
  linkedJobNumber?: string | null;
  /**
   * Already on the wire — the detail handler spreads the whole row and
   * `findById` takes no `select`. Declared here so the drawer can prefer the fresh
   * detail over the cursor-paginated list snapshot after a link or unlink.
   */
  attributionConfidence?: 'CLICK_ATTRIBUTED' | 'INFERRED' | 'MANUAL' | 'UNATTRIBUTED' | null;
  ambiguousCandidateCount?: number | null;
  sentimentTags?: string[] | null;
}

export interface TestimonialDetail {
  id: string;
  tenantId: string;
  appointmentId?: string | null;
  rating: number;
  text?: string | null;
  author?: string | null;
  submittedAt: string;
  /** Cleared by a human for the anonymous public page. False until approved. */
  isApprovedForDisplay: boolean;
  approvedAt?: string | null;
  approvedByUserId?: string | null;
}

export interface ReviewResponseSentimentAnalysis {
  categories?: string[];
  summary?: string;
  notes?: Array<{ at: string; userId: string; note: string }>;
  escalation?: {
    flaggedAt: string;
    flaggedByUserId: string;
    comment: string;
  };
  raw?: Record<string, unknown>;
}

/**
 * Reply draft payload persisted on `ExternalReview.replyDraft` (also returned
 * inline by `POST /v2/review-platforms/replies/:id/draft`).
 */
export interface ReplyDraft {
  text: string;
  generatedAt?: string;
  model?: string;
}

export interface GenerateReplyDraftResult {
  text: string;
  aiGenerated: boolean;
}

// ─── Stats (computed client-side from list payloads) ─────────────────────────

export interface ReviewsStats {
  total: number;
  averageRating: number | null;
  perPlatform: Array<{
    platform: ReviewPlatform | 'INTERNAL';
    count: number;
    average: number | null;
  }>;
  /** 0–100, % of replyable reviews (Google / Facebook external) that have a reply. */
  responseRate: number | null;
  /** Hours; null when the sample is too small. */
  medianTimeToReplyHours: number | null;
  /** 30 buckets, oldest → newest, count of reviews per UTC day. */
  trend30d: Array<{ date: string; count: number; averageRating: number | null }>;
}

// ─── Funnel baseline (spec §9, NOT-576 P0.9) ─────────────────────────────────
//
// Mirrors `ReviewFunnelBaseline` in
// `apps/operations/src/modules/sales/contracts/reviews/review-funnel-metrics.service.ts`,
// with the two `Date`s as the ISO strings they serialise to.
//
// Several of these fields exist to say the numbers are incomplete, and the
// screen has to render them rather than the totals alone: `posted` is null
// rather than zero for a tenant we cannot read back, `unmeasured` names stages
// with no data source at all, and `unreadablePlatforms` /
// `awaitingFirstSyncPlatforms` name the platforms missing from the counts.

export interface ChannelFunnel {
  asks: number;
  delivered: number;
  landed: number;
  /** `landed / delivered`, or null when nothing was delivered. */
  landingRate: number | null;
}

export interface PlatformTapped {
  taps: number;
  completedVisits: number;
  perCompletedVisit: number | null;
}

export interface PostedAttribution {
  total: number;
  attributed: number;
  inferred: number;
  manual: number;
  ambiguous: number;
  unattributed: number;
  unclassified: number;
  /** `attributed / total` — attribution COVERAGE, not a stage count. */
  coverage: number | null;
  /** Platforms whose reviews we cannot read back, so they are missing here. */
  unreadablePlatforms: ReviewPlatform[];
  /** Connected, but whose first sync has not landed yet. */
  awaitingFirstSyncPlatforms: ReviewPlatform[];
}

export interface ReviewFunnelBaseline {
  window: { from: string; to: string };
  completedVisits: number;
  asked: { total: number; stopped: number };
  asksPerCompletedVisit: number | null;
  landingByChannel: {
    email: ChannelFunnel;
    sms: ChannelFunnel;
    both: { asks: number; landed: number; unattributable: string };
  };
  rated: { responded: number; rateOfLanded: number | null };
  platformTapped: PlatformTapped;
  /** Null — never zero — when reviews cannot be read back for this tenant. */
  posted: PostedAttribution | null;
  /** Stage → why it has no data source yet. Never rendered as a zero. */
  unmeasured: Record<string, string>;
}

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface AddInternalNoteDto {
  note: string;
}

export interface FlagReviewDto {
  comment: string;
}

export interface PostReplyDto {
  text: string;
}

export interface GenerateReplyDraftDto {
  tone?: string;
  maxLength?: number;
}
